import { useState, useEffect } from "react";
import axios from "axios";
import ProductCard from "../components/ProductCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) { console.error("Error fetching products:", error); }
    finally { setLoading(false); }
  };

  const retailProducts = products.filter((p) => p.category === "retail");
  const wholesaleProducts = products.filter((p) => p.category === "wholesale");
  const getFilteredProducts = () => {
    if (activeCategory === "retail") return retailProducts;
    if (activeCategory === "wholesale") return wholesaleProducts;
    return products;
  };

  return (
    <div data-testid="products-page" className="min-h-screen bg-ivory pt-24">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-b from-champagne-light to-cream">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="font-display text-xs tracking-[0.4em] uppercase text-royal-gold mb-4 animate-fade-up">Our Treasures</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-charcoal mb-6 animate-fade-up" style={{ animationDelay: '0.2s' }}>
            The <span className="text-gold-gradient">Collection</span>
          </h1>
          <p className="font-body text-charcoal/60 max-w-xl mx-auto animate-fade-up" style={{ animationDelay: '0.4s' }}>
            Discover our exquisite range of handcrafted silver jewellery
          </p>
          <div className="w-24 h-px bg-gradient-to-r from-transparent via-royal-gold to-transparent mx-auto mt-8" />
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 bg-white border-y border-gold-muted/10">
        <div className="max-w-7xl mx-auto px-4 flex flex-wrap items-center justify-center gap-4">
          {[{ key: "all", label: "All Pieces" }, { key: "retail", label: "Retail Collection" }, { key: "wholesale", label: "Wholesale Selection" }].map((cat) => (
            <button key={cat.key} onClick={() => setActiveCategory(cat.key)} data-testid={`filter-${cat.key}`}
              className={`font-display text-xs tracking-[0.2em] uppercase px-6 py-3 transition-all ${activeCategory === cat.key ? "bg-royal-gold text-white" : "text-charcoal/60 hover:text-royal-gold border border-charcoal/20 hover:border-royal-gold"
                }`}>{cat.label}</button>
          ))}
        </div>
      </section>

      {/* Products */}
      <section className="py-16 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20">
              <div className="w-12 h-12 border-2 border-royal-gold border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="font-body text-charcoal/50">Loading collection...</p>
            </div>
          ) : getFilteredProducts().length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-6 border border-royal-gold/30 flex items-center justify-center">
                <span className="font-serif text-3xl text-royal-gold/50">✦</span>
              </div>
              <h3 className="font-serif text-2xl text-charcoal mb-2">Collection Awaits</h3>
              <p className="font-body text-charcoal/50">Our artisans are preparing new masterpieces</p>
            </div>
          ) : activeCategory === "all" ? (
            <>
              {retailProducts.length > 0 && (
                <div className="mb-20" data-testid="retail-section">
                  <div className="flex items-center mb-12">
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent to-royal-gold/30" />
                    <div className="px-8 text-center">
                      <p className="font-display text-xs tracking-[0.4em] uppercase text-royal-gold mb-1">For Collectors</p>
                      <h2 className="font-serif text-2xl text-charcoal">Retail <span className="text-gold-gradient">Collection</span></h2>
                    </div>
                    <div className="flex-1 h-px bg-gradient-to-l from-transparent to-royal-gold/30" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                    {retailProducts.map((product, index) => <ProductCard key={product.id} product={product} index={index} />)}
                  </div>
                </div>
              )}
              {wholesaleProducts.length > 0 && (
                <div data-testid="wholesale-section">
                  <div className="flex items-center mb-12">
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent to-silver/50" />
                    <div className="px-8 text-center">
                      <p className="font-display text-xs tracking-[0.4em] uppercase text-silver-dark mb-1">For Trade</p>
                      <h2 className="font-serif text-2xl text-charcoal">Wholesale <span className="text-gold-gradient">Selection</span></h2>
                    </div>
                    <div className="flex-1 h-px bg-gradient-to-l from-transparent to-silver/50" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                    {wholesaleProducts.map((product, index) => <ProductCard key={product.id} product={product} index={index + retailProducts.length} />)}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {getFilteredProducts().map((product, index) => <ProductCard key={product.id} product={product} index={index} />)}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-champagne via-champagne-light to-champagne">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-serif text-2xl text-charcoal mb-4">Interested in <span className="text-gold-gradient">Bulk Orders</span>?</h2>
          <p className="font-body text-charcoal/60 mb-8">Contact us for wholesale pricing and custom orders</p>
          <a href="https://wa.me/919104054259?text=Hello,%20I'm%20interested%20in%20wholesale%20orders." target="_blank" rel="noopener noreferrer" data-testid="wholesale-enquiry-btn" className="btn-luxury inline-block">Enquire for Wholesale</a>
        </div>
      </section>
    </div>
  );
};

export default Products;
